import type { Runner as Interface } from "../../design/services";
import type { Config, Action } from "../../design/types";
import type { Context } from "../../design/app";

export class Runner implements Interface {
    private _app: Record<string, unknown> = {};

    get app(): Record<string, unknown> { return this._app; }

    set app(context: Record<string, unknown>) {
        this._app = context;
    }

    constructor(_config: Config) {
    }

    ["constructor"](config: Config): Runner {
        return new Runner(config);
    }

    run(action: Action, context: Context, params?: Record<string, unknown>): unknown {
        // Validate inputs
        if (!action || typeof action !== 'object' || Array.isArray(action)) {
            throw new Error('Invalid action: must be an object');
        }
        if (!context || typeof context !== 'object' || Array.isArray(context)) {
            throw new Error('Invalid context: must be an object');
        }

        const page = {
            data: params?.data,
            id: params?.id,
            model: params?.model,
            path: context.page?.current?.path ?? [],
        };
        const app = {
            ...(context as unknown as Record<string, unknown>),
            navigate: params?.navigate,
        };

        try {
            // Determine if the code is async
            const code = action.code || '';
            const isAsync = code.includes('await') || code.includes('Promise');

            // Create the function
            // Note: True isolation (preventing global variable creation) is not
            // fully achievable with Function constructor without strict mode,
            // which would break code that relies on implicit global variable assignment
            if (isAsync) {
                const asyncFn = new Function(
                    'page',
                    'app',
                    `return (async () => { ${code} })();`
                );
                return asyncFn(page, app);
            } else {
                const syncFn = new Function(
                    'page',
                    'app',
                    code
                );
                return syncFn(page, app);
            }
        } catch (error) {
            // Re-throw the error with context
            if (error instanceof Error) {
                throw error;
            }
            throw new Error(String(error));
        }
    }

    evaluate(code: string, scope: Record<string, unknown>): unknown {
        try {
            // Create a function that has access to 'this' and 'entity' as the scope,
            // and 'app' as the application context
            const fn = new Function('entity', 'app', `
                const self = this;
                ${code}
            `);
            return fn.call(scope, scope, this._app);
        } catch (e) {
            console.error('Error evaluating code:', code, e);
            return undefined;
        }
    }
}
